import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { env } from "@/config/env";

interface User {
  id: number;
  email: string;
}

interface AuthState {
  _hasHydrated: boolean;
  token: string | null;
  user: User | null;
  permissions: Set<string>;
  superAdminRoleName: string;
  superAdminEmail: string;
  setHasHydrated: (value: boolean) => void;
  setAuth: (data: {
    token: string;
    user: User;
    permissions: Set<string>;
  }) => void;
  logout: () => void;
}

type PersistedShape = {
  token: string | null;
  user: User | null;
  permissions: string[];
  superAdminRoleName: string;
  superAdminEmail: string;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      _hasHydrated: false,
      token: null,
      user: null,
      permissions: new Set<string>(),
      superAdminRoleName: env.superAdminRoleName,
      superAdminEmail: env.superAdminEmail,

      setHasHydrated: (value) => set({ _hasHydrated: value }),

      setAuth: (data) =>
        set({
          token: data.token,
          user: data.user,
          permissions: new Set(data.permissions),
        }),

      logout: () =>
        set({
          token: null,
          user: null,
          permissions: new Set<string>(),
        }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage<PersistedShape>(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        permissions: Array.from(state.permissions),
        superAdminRoleName: state.superAdminRoleName,
        superAdminEmail: state.superAdminEmail,
      }),
      merge: (persistedState, currentState) => {
        const typed = persistedState as PersistedShape | undefined;

        return {
          ...currentState,
          ...typed,
          permissions: new Set(typed?.permissions ?? []),
        };
      },
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);